import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, Maximize, Mail, Heart, Presentation } from "lucide-react";
import DonateForm from "@/components/home/DonateForm";
import { useMediaHold } from "@/lib/scroll";

const SLIDES = Array.from({ length: 11 }, (_, i) => `/deck/slide-${String(i + 1).padStart(2, "0")}.jpg`);
const TOTAL = SLIDES.length + 1;
const AUTOPLAY_MS = 10000;

function ContactSlide() {
  return (
    <div data-testid="deck-contact-slide" className="w-full h-full bg-ink flex flex-col justify-center px-10 sm:px-20 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 flex h-3">
        <span className="flex-1 bg-royal" />
        <span className="flex-1 bg-blood" />
      </div>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-6">Fund Raising — Get in touch</p>
      <h3 className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9]">
        Contact:<br /><span className="text-gray-400">JC</span>
      </h3>
      <a
        href="mailto:bosshaiti@gmail.com"
        data-testid="deck-contact-email"
        className="mt-10 inline-flex items-center gap-3 font-mono text-lg sm:text-2xl text-blood hover:text-white transition-colors"
      >
        <Mail size={22} /> bosshaiti@gmail.com
      </a>
      <p className="mt-16 font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gray-500">
        The Zo Pound Experience — A Simplicity Films, LLC Production
      </p>
    </div>
  );
}

export default function FundraisingDeck({ open, onClose }) {
  useMediaHold(open);
  const [idx, setIdx] = useState(0);
  const [view, setView] = useState("slides");
  const [auto, setAuto] = useState(true);
  const [music, setMusic] = useState(false);
  const audioRef = useRef(null);
  const stageRef = useRef(null);

  const go = useCallback((d) => setIdx((i) => (i + d + TOTAL) % TOTAL), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go, onClose]);

  useEffect(() => {
    if (!open || !auto || view !== "slides") return;
    const t = setTimeout(() => go(1), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [open, auto, idx, go, view]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (open && music) a.play().catch(() => setMusic(false));
    else a.pause();
  }, [open, music]);

  useEffect(() => {
    setIdx(0);
    setView("slides");
    setAuto(true);
    setMusic(open);
    if (!open && audioRef.current) audioRef.current.currentTime = 0;
  }, [open]);

  const fullscreen = () => stageRef.current?.requestFullscreen?.();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex flex-col"
          data-testid="fundraising-deck-modal"
        >
          <div className="flex items-center justify-between px-4 sm:px-8 h-16 border-b border-white/10">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 bg-blood" />
              <p className="font-display font-bold uppercase tracking-wider text-sm sm:text-base">
                Fund Raising <span className="text-gray-500 font-mono text-xs ml-2">EPK Deck</span>
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <button data-testid="deck-donate-toggle" onClick={() => setView((v) => (v === "donate" ? "slides" : "donate"))}
                className={`flex items-center gap-2 font-display font-bold uppercase tracking-wider text-xs sm:text-sm px-3 sm:px-4 py-2 transition-colors ${view === "donate" ? "border border-white/30 text-white hover:border-blood" : "bg-blood hover:bg-red-700 text-white"}`}>
                {view === "donate" ? <><Presentation size={14} /> <span className="hidden sm:inline">Back to</span> Deck</> : <><Heart size={14} fill="currentColor" /> Donate</>}
              </button>
              <button data-testid="deck-music-toggle" onClick={() => setMusic((m) => !m)} title="Soundtrack"
                className={`p-2 transition-colors ${music ? "text-blood" : "text-gray-400 hover:text-white"}`}>
                {music ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <button data-testid="deck-autoplay-toggle" onClick={() => setAuto((a) => !a)} title="Auto-advance"
                className={`p-2 transition-colors ${auto ? "text-blood" : "text-gray-400 hover:text-white"}`}>
                {auto ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <button data-testid="deck-fullscreen-button" onClick={fullscreen} title="Fullscreen"
                className="hidden sm:block p-2 text-gray-400 hover:text-white transition-colors">
                <Maximize size={18} />
              </button>
              <button data-testid="deck-close-button" onClick={onClose} aria-label="Close presentation"
                className="p-2 text-gray-400 hover:text-blood transition-colors">
                <X size={24} />
              </button>
            </div>
          </div>

          {view === "donate" ? (
            <div className="flex-1 min-h-0 flex items-stretch justify-center px-2 sm:px-16 py-4">
              <div className="w-full max-w-5xl bg-black border border-white/10 clip-frame overflow-hidden">
                <DonateForm />
              </div>
            </div>
          ) : (
          <div className="flex-1 flex items-center justify-center px-2 sm:px-16 py-4 relative min-h-0">
            <button data-testid="deck-prev-button" onClick={() => go(-1)} aria-label="Previous slide"
              className="absolute left-1 sm:left-4 z-10 p-2 sm:p-3 bg-black/60 border border-white/15 text-gray-300 hover:text-white hover:border-blood transition-colors">
              <ChevronLeft size={22} />
            </button>
            <div ref={stageRef} className="w-full max-w-[min(100%,calc((100vh-13rem)*16/9))] aspect-video bg-black border border-white/10 relative overflow-hidden clip-frame">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0"
                  data-testid={`deck-slide-${idx + 1}`}
                >
                  {idx < SLIDES.length ? (
                    <img src={SLIDES[idx]} alt={`Fund raising deck slide ${idx + 1}`} className="w-full h-full object-contain bg-black" />
                  ) : (
                    <ContactSlide />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            <button data-testid="deck-next-button" onClick={() => go(1)} aria-label="Next slide"
              className="absolute right-1 sm:right-4 z-10 p-2 sm:p-3 bg-black/60 border border-white/15 text-gray-300 hover:text-white hover:border-blood transition-colors">
              <ChevronRight size={22} />
            </button>
          </div>
          )}

          {view === "slides" && (
          <div className="px-4 sm:px-8 pb-4 flex items-center gap-4">
            <span data-testid="deck-slide-counter" className="font-mono text-xs text-gray-400 tabular-nums shrink-0">
              {String(idx + 1).padStart(2, "0")} / {TOTAL}
            </span>
            <div className="flex-1 flex gap-1.5 overflow-x-auto">
              {Array.from({ length: TOTAL }, (_, i) => (
                <button key={i} data-testid={`deck-dot-${i + 1}`} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 flex-1 min-w-3 transition-colors duration-300 ${i === idx ? "bg-blood" : i < idx ? "bg-white/40" : "bg-white/15 hover:bg-white/30"}`} />
              ))}
            </div>
            <span className="hidden sm:block font-mono text-[10px] uppercase tracking-widest text-gray-600 shrink-0">← → to navigate</span>
          </div>
          )}

          <audio ref={audioRef} src="/deck/soundtrack.mp3" loop preload="auto" data-testid="deck-soundtrack" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
